import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setMediaGroup(this.dataset.activeMediaGroup || 'products', { animate: false });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  setMediaGroup(group) {
    this.#setMediaGroup(group, { animate: true });
  }

  async #setMediaGroup(group, { animate = false } = {}) {
    const requestedGroup = group === 'scene' ? 'scene' : 'products';
    const requestedSlides = this.querySelectorAll(`slideshow-slide[data-media-group="${requestedGroup}"]`);
    const normalizedGroup = requestedSlides.length ? requestedGroup : 'products';
    const segmentButtons = this.querySelectorAll('.product-media-gallery__segment-button');
    const activeSlides = this.querySelectorAll(`slideshow-slide[data-media-group="${normalizedGroup}"]`);
    const groupItems = this.querySelectorAll('[data-media-group]');
    const targetSlide = activeSlides[0];
    const targetSlideId = targetSlide?.getAttribute('slide-id');
    const previousGroup = this.dataset.activeMediaGroup || 'products';

    this.dataset.activeMediaGroup = normalizedGroup;

    segmentButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const isActive = button.dataset.mediaGroup === normalizedGroup;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', `${isActive}`);
    });

    groupItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      if (!item.dataset.mediaGroup) return;
      if (item.classList.contains('product-media-gallery__segment-button')) return;
      if (item instanceof HTMLElement && item.tagName.toLowerCase() === 'slideshow-slide') return;

      item.hidden = item.dataset.mediaGroup !== normalizedGroup;
    });

    this.querySelectorAll('[ref="total"]').forEach((total) => {
      total.textContent = `${activeSlides.length}`;
    });

    if (animate && targetSlideId && previousGroup !== normalizedGroup) {
      this.querySelectorAll('[ref="current"]').forEach((current) => {
        current.textContent = '1';
      });

      await this.#animateMediaGroupTransition(normalizedGroup, targetSlide);
    }

    this.querySelectorAll('slideshow-slide[data-media-group]').forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;

      slide.hidden = slide.dataset.mediaGroup !== normalizedGroup;
      slide.removeAttribute('reveal');
    });

    this.slideshow?.select(0, undefined, { animate: false });
  }

  #animateMediaGroupTransition(nextGroup, targetSlide) {
    const slideshow = this.slideshow;
    const slideshowContainer = slideshow?.refs.slideshowContainer;
    const currentSlide = slideshow?.slides?.[slideshow.current] || this.querySelector('slideshow-slide[aria-hidden="false"]');

    if (!(slideshowContainer instanceof HTMLElement)) return Promise.resolve();
    if (!(targetSlide instanceof HTMLElement)) return Promise.resolve();
    if (!(currentSlide instanceof HTMLElement)) return Promise.resolve();

    const currentImage = currentSlide.querySelector('.product-media__image');
    const nextImage = targetSlide.querySelector('.product-media__image');

    if (!(currentImage instanceof HTMLImageElement) || !(nextImage instanceof HTMLImageElement)) {
      return Promise.resolve();
    }

    const direction = nextGroup === 'scene' ? 'forward' : 'backward';
    const transition = document.createElement('div');
    const currentFrame = document.createElement('div');
    const nextFrame = document.createElement('div');
    const currentClone = currentImage.cloneNode(true);
    const nextClone = nextImage.cloneNode(true);

    transition.className = 'product-media-gallery__group-transition';
    transition.dataset.direction = direction;
    currentFrame.className = 'product-media-gallery__group-transition-frame product-media-gallery__group-transition-frame--current';
    nextFrame.className = 'product-media-gallery__group-transition-frame product-media-gallery__group-transition-frame--next';

    return new Promise((resolve) => {
      const timeout = window.setTimeout(done, 650);

      function done() {
        window.clearTimeout(timeout);
        transition.removeEventListener('transitionend', done);
        transition.remove();
        resolve();
      }

      currentClone.removeAttribute('ref');
      nextClone.removeAttribute('ref');
      currentClone.setAttribute('loading', 'eager');
      nextClone.setAttribute('loading', 'eager');
      currentFrame.append(currentClone);
      nextFrame.append(nextClone);
      transition.append(currentFrame, nextFrame);
      slideshowContainer.append(transition);

      transition.addEventListener('transitionend', done, { once: true });

      requestAnimationFrame(() => {
        transition.classList.add('is-active');
      });
    });
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
